/**
 * Gamification Service
 * 
 * Servizio per gestire il sistema di gamification di StudierAI:
 * - Lettura profilo gamification da Firestore (real-time con onSnapshot)
 * - Chiamate API per registrare eventi
 * - Utilities per calcoli e formattazione
 * 
 * Il profilo gamification viene letto direttamente da Firestore per performance
 * e aggiornamenti real-time. Gli eventi vengono registrati tramite API backend
 * per garantire atomicità e consistenza.
 */

import { doc, onSnapshot, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '@redux/firebase';
import { logError } from '@utils/logger';

// Costanti
const COLLECTION_NAME = 'gamificationProfiles';

// Determina l'URL API in base all'ambiente
let API_BASE_URL = '';

// Prima prova con le variabili ambiente
if (process.env.REACT_APP_ENVIRONMENT === "development") {
  API_BASE_URL = "http://" + (process.env.REACT_APP_API_URL_DEVELOPMENT || "localhost:8080");
} else if (process.env.REACT_APP_ENVIRONMENT === "preproduction") {
  API_BASE_URL = "https://" + (process.env.REACT_APP_API_URL_PREPRODUCTION || "");
} else if (process.env.REACT_APP_ENVIRONMENT === "production") {
  API_BASE_URL = "https://" + (process.env.REACT_APP_API_URL_PRODUCTION || "");
}

// Fallback: se NODE_ENV è development e API_BASE_URL è vuoto, usa localhost
if (!API_BASE_URL || API_BASE_URL === 'http://' || API_BASE_URL === 'https://') {
  if (process.env.NODE_ENV === 'development') {
    API_BASE_URL = 'http://localhost:8080';
  }
}

// Tier delle icone streak
export const ICON_TIERS = {
  NONE: 'NONE',         // streak = 0
  SPARK: 'SPARK',       // 1 <= streak < 7
  FIRE: 'FIRE',         // 7 <= streak < 30
  BLUE_FIRE: 'BLUE_FIRE' // streak >= 30
};

// Configurazione Scudi
export const MAX_SHIELDS = 3;  // Massimo scudi accumulabili
export const DEFAULT_SHIELDS = 3;  // Scudi iniziali per nuovi utenti

// Tipi di evento
export const EVENT_TYPES = {
  ANSWER: 'ANSWER',
  REVIEW_EXAM: 'REVIEW_EXAM',
  FIRST_DOC_UPLOAD: 'FIRST_DOC_UPLOAD',
  FIRST_EXAM_COMPLETED: 'FIRST_EXAM_COMPLETED',
  DAILY_ACTIVITY: 'DAILY_ACTIVITY'  // Traccia attività giornaliera (0 punti base, aggiorna streak)
};

// Esiti delle risposte
export const ANSWER_OUTCOMES = {
  CORRECT: 'CORRECT',
  WRONG: 'WRONG'
};

/**
 * Profilo gamification di default per nuovi utenti
 */
export const DEFAULT_GAMIFICATION_PROFILE = {
  userId: null,
  totalPointsLifetime: 0,
  currentStreak: 0,
  longestStreak: 0,
  lastActiveDate: null,
  lastStreakDayAwarded: null,
  iconTier: ICON_TIERS.NONE,
  onboardingChecklist: {
    firstDocUploaded: false,
    firstExamCompleted: false,
    firstReviewCompleted: false
  },
  shields: DEFAULT_SHIELDS,  // 3 scudi di default
  createdAt: null,
  updatedAt: null
};

/**
 * Sottoscrive agli aggiornamenti real-time del profilo gamification
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @param {function} onUpdate - Callback chiamata quando il profilo viene aggiornato
 * @param {function} onError - Callback per gestire errori
 * @returns {function} Funzione per annullare la sottoscrizione
 */
export function subscribeToGamificationProfile(userId, onUpdate, onError) {
  if (!userId) {
    return () => {};
  }

  const docRef = doc(firestore, COLLECTION_NAME, userId);

  const unsubscribe = onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        onUpdate({
          ...DEFAULT_GAMIFICATION_PROFILE,
          ...data,
          exists: true
        });
      } else {
        // Profilo non esiste ancora - primo accesso
        onUpdate({
          ...DEFAULT_GAMIFICATION_PROFILE,
          userId,
          exists: false,
          isFirstAccess: true
        });
      }
    },
    (error) => {
      logError('Error subscribing to gamification profile:', error);
      if (onError) {
        onError(error);
      }
    }
  );

  return unsubscribe;
}

/**
 * Ottiene il profilo gamification una volta (senza sottoscrizione)
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Profilo gamification
 */
export async function getGamificationProfile(userId) {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const docRef = doc(firestore, COLLECTION_NAME, userId);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      return {
        ...DEFAULT_GAMIFICATION_PROFILE,
        ...snapshot.data(),
        exists: true
      };
    } else {
      return {
        ...DEFAULT_GAMIFICATION_PROFILE,
        userId,
        exists: false,
        isFirstAccess: true
      };
    }
  } catch (error) {
    logError('Error getting gamification profile:', error);
    throw error;
  }
}

/**
 * Crea un nuovo profilo gamification per un utente
 * Viene chiamato al primo accesso per inizializzare il documento
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Profilo creato
 */
export async function createGamificationProfile(userId, fullName) {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const docRef = doc(firestore, COLLECTION_NAME, userId);
    const snapshot = await getDoc(docRef);
    
    // Se il profilo esiste già, restituiscilo
    if (snapshot.exists()) {
      return {
        ...DEFAULT_GAMIFICATION_PROFILE,
        ...snapshot.data(),
        exists: true
      };
    }
    
    // Crea il nuovo profilo
    const newProfile = {
      userId,
      totalPointsLifetime: 0,
      currentStreak: 0,
      fullName: fullName || "",
      longestStreak: 0,
      lastActiveDate: null,
      lastStreakDayAwarded: null,
      iconTier: ICON_TIERS.NONE,
      onboardingChecklist: {
        firstDocUploaded: false,
        firstExamCompleted: false,
        firstReviewCompleted: false
      },
      shields: DEFAULT_SHIELDS,  // 3 scudi di default
      // STU-479: il nome in classifica si mostra solo se lo studente lo
      // sceglie (privacy di default per i minorenni). I profili creati prima
      // non hanno il campo e restano come sono: decide Giovanni.
      nomeInClassifica: false,
      needsOnboarding: true, // Flag per mostrare il modale di onboarding
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(docRef, newProfile);
    
    return {
      ...DEFAULT_GAMIFICATION_PROFILE,
      ...newProfile,
      exists: true
    };
  } catch (error) {
    logError('Error creating gamification profile:', error);
    throw error;
  }
}

/**
 * Mostra o nasconde il nome dello studente nella classifica nazionale
 * (STU-479). La classifica lo legge al giro della notte (functions
 * nationalRanking.js): false = compare come "Studente".
 *
 * @param {string} userId
 * @param {boolean} visibile
 */
export async function impostaNomeInClassifica(userId, visibile) {
  if (!userId) throw new Error('userId is required');
  const docRef = doc(firestore, COLLECTION_NAME, userId);
  await updateDoc(docRef, { nomeInClassifica: Boolean(visibile), updatedAt: serverTimestamp() });
}

/**
 * Registra un evento di gamification
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @param {string} eventType - Tipo di evento (ANSWER, REVIEW_EXAM, ecc.)
 * @param {string} outcome - Esito (solo per ANSWER: CORRECT/WRONG)
 * @returns {Promise<object>} Risultato dell'operazione con punti guadagnati
 */
export async function recordGamificationEvent(userId, eventType, outcome = null) {
  if (!userId) {
    throw new Error('userId is required');
  }

  if (!Object.values(EVENT_TYPES).includes(eventType)) {
    throw new Error(`Invalid eventType: ${eventType}`);
  }

  try {
    const url = `${API_BASE_URL}/api/gamification/record-event`;
    const payload = { userId, eventType, outcome };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error ${response.status}`);
    }

    const result = await response.json();
    
    return result;
  } catch (error) {
    logError('Error recording gamification event:', error);
    throw error;
  }
}

/**
 * Registra una risposta corretta
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Risultato con punti guadagnati
 */
export async function recordCorrectAnswer(userId) {
  return recordGamificationEvent(userId, EVENT_TYPES.ANSWER, ANSWER_OUTCOMES.CORRECT);
}

/**
 * Registra una risposta sbagliata (conta come giorno attivo ma 0 punti base)
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Risultato con punti guadagnati
 */
export async function recordWrongAnswer(userId) {
  return recordGamificationEvent(userId, EVENT_TYPES.ANSWER, ANSWER_OUTCOMES.WRONG);
}

/**
 * Registra la visualizzazione del resoconto esame
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Risultato con punti guadagnati
 */
export async function recordExamReview(userId) {
  return recordGamificationEvent(userId, EVENT_TYPES.REVIEW_EXAM);
}

/**
 * Registra il primo caricamento documento
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Risultato con punti guadagnati
 */
export async function recordFirstDocUpload(userId) {
  return recordGamificationEvent(userId, EVENT_TYPES.FIRST_DOC_UPLOAD);
}

/**
 * Registra il primo esame completato
 *
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Risultato con punti guadagnati
 */
export async function recordFirstExamCompleted(userId) {
  return recordGamificationEvent(userId, EVENT_TYPES.FIRST_EXAM_COMPLETED);
}

/**
 * Registra attività giornaliera (per aggiornare la streak senza guadagnare punti base)
 * Chiamato automaticamente quando l'utente usa l'app (dashboard, materiali, chat, ecc.)
 *
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<object>} Risultato con streak aggiornata
 */
export async function recordDailyActivity(userId) {
  return recordGamificationEvent(userId, EVENT_TYPES.DAILY_ACTIVITY);
}

/**
 * Calcola il tier dell'icona in base alla streak
 * 
 * @param {number} streak - Numero di giorni consecutivi
 * @returns {string} Tier dell'icona
 */
export function calculateIconTier(streak) {
  if (streak <= 0) return ICON_TIERS.NONE;
  if (streak < 7) return ICON_TIERS.SPARK;
  if (streak < 30) return ICON_TIERS.FIRE;
  return ICON_TIERS.BLUE_FIRE;
}

/**
 * Calcola il bonus streak atteso per un giorno futuro
 * 
 * @param {number} currentStreak - Streak attuale
 * @param {number} daysFromNow - Giorni da oggi (0 = oggi, 1 = domani, ecc.)
 * @returns {number} Bonus atteso
 */
export function calculateExpectedBonus(currentStreak, daysFromNow = 0) {
  if (currentStreak === 0) {
    // Se streak è 0, il bonus atteso per D+N è min(N, 30)
    return Math.min(daysFromNow, 30);
  }
  // Se oggi Streak = S, il bonus atteso per D+N è min(S+N, 30)
  return Math.min(currentStreak + daysFromNow, 30);
}

/**
 * Calcola la data di ieri nel formato YYYY-MM-DD in timezone Europe/Rome
 */
export function getYesterdayKey() {
  const now = new Date();
  // Converti in timezone Europe/Rome
  const romeTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  // Sottrai un giorno
  romeTime.setDate(romeTime.getDate() - 1);
  const year = romeTime.getFullYear();
  const month = String(romeTime.getMonth() + 1).padStart(2, '0');
  const day = String(romeTime.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calcola il bonus streak che l'utente guadagnerà rispondendo oggi
 * Tiene conto di lastActiveDate per determinare se la streak è attiva o rotta
 * 
 * @param {number} currentStreak - Streak attuale
 * @param {string|null} lastActiveDate - Ultimo giorno attivo (YYYY-MM-DD)
 * @param {string|null} lastStreakDayAwarded - Ultimo giorno in cui è stato assegnato il bonus
 * @returns {number} Bonus che l'utente guadagnerà rispondendo oggi
 */
export function calculateExpectedBonusToday(currentStreak, lastActiveDate, lastStreakDayAwarded) {
  const todayKey = getTodayKey();
  const yesterdayKey = getYesterdayKey();
  
  // Se ha già guadagnato il bonus oggi, non può guadagnare altro
  if (lastStreakDayAwarded === todayKey) {
    return 0;
  }
  
  // Se lastActiveDate è null (utente nuovo), il primo giorno non dà bonus streak
  if (lastActiveDate === null) {
    return 0;
  }
  
  // Se ha risposto ieri, la streak continua: bonus = currentStreak + 1
  if (lastActiveDate === yesterdayKey) {
    const bonus = Math.min(currentStreak + 1, 30);
    return bonus;
  }
  
  // Se ha risposto oggi, la streak è già stata incrementata oggi
  // Il bonus sarà 0 se già preso, altrimenti sarebbe currentStreak
  if (lastActiveDate === todayKey) {
    return 0; // Già attivo oggi, bonus già assegnato
  }
  
  // Streak rotta (non ha risposto da più di 1 giorno): reset a 0, nessun bonus
  return 0;
}

/**
 * Verifica se l'utente ha completato la checklist di onboarding
 * 
 * @param {object} checklist - Oggetto checklist
 * @returns {boolean} true se tutti gli item sono completati
 */
export function isOnboardingComplete(checklist) {
  if (!checklist) return false;
  return (
    checklist.firstDocUploaded === true &&
    checklist.firstExamCompleted === true &&
    checklist.firstReviewCompleted === true
  );
}

/**
 * Calcola la percentuale di completamento della checklist
 * 
 * @param {object} checklist - Oggetto checklist
 * @returns {number} Percentuale (0-100)
 */
export function getOnboardingProgress(checklist) {
  if (!checklist) return 0;
  
  const items = [
    checklist.firstDocUploaded,
    checklist.firstExamCompleted,
    checklist.firstReviewCompleted
  ];
  
  const completed = items.filter(Boolean).length;
  return Math.round((completed / items.length) * 100);
}

/**
 * Formatta i punti per la visualizzazione
 * 
 * @param {number} points - Numero di punti
 * @returns {string} Punti formattati (es. "1.234")
 */
export function formatPoints(points) {
  if (typeof points !== 'number') return '0';
  return points.toLocaleString('it-IT');
}

/**
 * Ottiene la data di oggi nel formato YYYY-MM-DD (timezone Europe/Rome)
 * 
 * @returns {string} Data formattata
 */
export function getTodayKey() {
  const now = new Date();
  // Converti in timezone Europe/Rome
  const romeTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = romeTime.getFullYear();
  const month = String(romeTime.getMonth() + 1).padStart(2, '0');
  const day = String(romeTime.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Verifica se l'utente ha già guadagnato il bonus streak oggi
 * 
 * @param {string} lastStreakDayAwarded - Data ultimo bonus (YYYY-MM-DD)
 * @returns {boolean} true se il bonus è già stato assegnato oggi
 */
export function hasEarnedBonusToday(lastStreakDayAwarded) {
  if (!lastStreakDayAwarded) return false;
  return lastStreakDayAwarded === getTodayKey();
}

/**
 * Verifica se l'utente è già attivo oggi
 * 
 * @param {string} lastActiveDate - Data ultimo giorno attivo (YYYY-MM-DD)
 * @returns {boolean} true se l'utente ha già risposto oggi
 */
export function isActiveToday(lastActiveDate) {
  if (!lastActiveDate) return false;
  return lastActiveDate === getTodayKey();
}

/**
 * Segna l'onboarding come completato in Firestore
 * Imposta needsOnboarding a false
 * 
 * @param {string} userId - Firebase UID dell'utente
 * @returns {Promise<void>}
 */
export async function markOnboardingComplete(userId) {
  if (!userId) {
    return;
  }

  try {
    const docRef = doc(firestore, COLLECTION_NAME, userId);
    await setDoc(docRef, {
      needsOnboarding: false,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    logError('Error marking onboarding complete:', error);
  }
}

const gamificationService = {
  // Subscription
  subscribeToGamificationProfile,
  getGamificationProfile,
  createGamificationProfile,

  // Event recording
  recordGamificationEvent,
  recordCorrectAnswer,
  recordWrongAnswer,
  recordExamReview,
  recordFirstDocUpload,
  recordFirstExamCompleted,
  recordDailyActivity,

  // Utilities
  calculateIconTier,
  calculateExpectedBonus,
  calculateExpectedBonusToday,
  isOnboardingComplete,
  getOnboardingProgress,
  formatPoints,
  getTodayKey,
  getYesterdayKey,
  hasEarnedBonusToday,
  isActiveToday,
  markOnboardingComplete,

  // Constants
  ICON_TIERS,
  EVENT_TYPES,
  ANSWER_OUTCOMES,
  DEFAULT_GAMIFICATION_PROFILE,
  MAX_SHIELDS,
  DEFAULT_SHIELDS
};

export default gamificationService;

